<script lang="ts">
  import OpeningHours from "$lib/components/details/opening-hours.svelte";
  import { Badge } from "$lib/components/ui/badge/index.js";
  import { Button } from "$lib/components/ui/button/index.js";
  import { Card, CardContent } from "$lib/components/ui/card/index.js";
  import * as Carousel from "$lib/components/ui/carousel/index.js";
  import Coupon from "$lib/components/ui/Coupon.svelte";
  import { Separator } from "$lib/components/ui/separator/index.js";
  import { formatDistance } from "$lib/helpers/format-distance";
  import { IsMobile } from "$lib/helpers/is-mobile.svelte";
  import { detailsStore } from "$lib/stores/details.svelte";
  import { locationStore } from "$lib/stores/location.svelte";
  import { stateStore } from "$lib/stores/state.svelte";
  import {
    BadgeCheck,
    Calendar,
    ChevronLeft,
    ChevronRight,
    Droplets,
    Gift,
    Globe,
    Hexagon,
    Leaf,
    Mail,
    MapPinned,
    Navigation,
    Phone,
    Undo2,
  } from "lucide-svelte";
  import DetailsIcon from "../ui/DetailsIcon.svelte";
  import HungaryPointIcon from "../ui/HungaryPointIcon.svelte";
  import LightBulbIcon from "../ui/LightBulbIcon.svelte";
  import RatingsPopover from "./ratings-popover.svelte";
  import PhoneClick from "../ui/PhoneClick.svelte";

  // Generate visible page numbers for pagination
  const visiblePages = $derived.by(() => {
    const total = detailsStore.totalCount;
    const current = detailsStore.currentPosition;
    const pages: (number | string)[] = [];

    if (total <= 7) {
      // Show all pages if 7 or less
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    if (current > 3) {
      pages.push("...");
    }

    // Show pages around current
    for (
      let i = Math.max(2, current - 1);
      i <= Math.min(total - 1, current + 1);
      i++
    ) {
      pages.push(i);
    }

    if (current < total - 2) {
      pages.push("...");
    }

    // Always show last page
    if (total > 1) {
      pages.push(total);
    }

    return pages;
  });

  function formatDate(dateString: string | undefined) {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  }

  function handleSocialClick(url: string | undefined) {
    if (url && url.trim()) {
      window.open(url.startsWith("http") ? url : `https://${url}`, "_blank");
    }
  }

  function handleEmailClick(email: string | undefined) {
    if (email && email.trim()) {
      window.location.href = `mailto:${email}`;
    }
  }

  function handlePhoneClick(phone: string | undefined) {
    if (phone && phone.trim()) {
      window.location.href = `tel:${phone}`;
    }
  }

  function handleClose() {
    locationStore.setSelectedPoi(null);
    detailsStore.clear();
    stateStore.closeDetail();
  }

  function handlePageClick(page: number | string) {
    if (typeof page === "number" && page !== detailsStore.currentPosition) {
      detailsStore.navigate(page - 1);
    }
  }

  let distance = $derived.by(() => {
    if (
      !locationStore.userLocation ||
      !detailsStore.currentPoiDetail?.latitude ||
      !detailsStore.currentPoiDetail?.longitude
    )
      return null;

    return formatDistance(
      locationStore.calculateDistance(
        {
          lat: detailsStore.currentPoiDetail?.latitude,
          lng: detailsStore.currentPoiDetail?.longitude,
        },
        locationStore.userLocation
      )
    );
  });

  function handleCenterClick(lat: number | undefined, lng: number | undefined) {
    if (!lat || !lng) return;

    const poiId = detailsStore.currentPoiDetail?.id?.toString();

    // Set the selected POI ID for highlighting
    locationStore.setSelectedPoi(poiId || null);

    // on mobile, let's close the drawer, on desktop, slightly center is to the right
    if (new IsMobile().current) {
      stateStore.activePage = null;
    } else {
      lng -= 0.0007;
    }
    locationStore.centerMapOn({ lat, lng }, 18);
  }

  let isMultiple = $derived(detailsStore.totalCount > 1);

  function handleCouponClick() {
    stateStore.openAppInstall(
      "Töltsd le az appot",
      "Ahhoz, hogy a kuponokat is meg tudd tekinteni és több funkciót tudj használni, töltsd le a Beeco mobil alkalmazását!"
    );
  }

  const poiIcon = $derived.by(async () => {
    const subCategoryPath = `/assets/icons/${detailsStore.currentPoiDetail?.subCategory.toLowerCase()}.svg`;
    const labelPath = `/assets/icons/${detailsStore.currentPoiDetail?.label.toLowerCase()}.svg`;

    try {
      const res = await fetch(subCategoryPath);
      if (
        res.ok &&
        res.headers.get("Content-Type")?.includes("image/svg+xml")
      ) {
        return subCategoryPath;
      } else {
        // fallback if subcategory svg doesn't exist
        return labelPath;
      }
    } catch (error) {
      // fetch failed (e.g., network issue), fallback to label
      return labelPath;
    }
  });
</script>

<div class="flex flex-col font-extrabold h-full bg-background rounded-t-md">
  <!-- Enhanced Header with Pagination -->
  <div
    class="sticky z-50 top-0 bg-secondary/95 backdrop-blur supports-[backdrop-filter]:bg-secondary/70"
  >
    <div class="flex items-center justify-between p-3">
      <!-- Close button and title -->
      <Button
        onclick={handleClose}
        class="cursor-pointer {isMultiple ? 'w-14' : 'w-auto'} sm:w-auto"
      >
        <Undo2 class="h-4 w-4" />
        <span class="{isMultiple ? 'hidden' : 'inline'} sm:inline">
          {!!stateStore.prevPage ? "Vissza" : "Bezárás"}
        </span>
      </Button>

      <!-- Pagination controls -->
      {#if isMultiple}
        <div class="flex items-center sm:w-auto w-full justify-center">
          <Button
            class="h-8 w-8 cursor-pointer"
            disabled={!detailsStore.canGoPrev || detailsStore.isLoading}
            onclick={() => detailsStore.navigate("prev")}
          >
            <ChevronLeft class="h-4 w-4" />
          </Button>

          <div class="flex items-center px-2">
            {#each visiblePages as page}
              {#if page === "..."}
                <span class="px-1 text-muted-foreground">...</span>
              {:else}
                <button
                  onclick={() => handlePageClick(page)}
                  disabled={detailsStore.isLoading}
                  class="cursor-pointer relative h-8 w-8 rounded-full flex items-center justify-center transition-all
                  {page === detailsStore.currentPosition
                    ? 'text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'}
                  disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {#if page === detailsStore.currentPosition}
                    <Hexagon class="absolute inset-0 h-8 w-8 fill-primary" />
                  {/if}
                  <span class="relative text-xs font-medium">{page}</span>
                </button>
              {/if}
            {/each}
          </div>

          <Button
            class="h-8 w-8 cursor-pointer"
            disabled={!detailsStore.canGoNext || detailsStore.isLoading}
            onclick={() => detailsStore.navigate("next")}
          >
            <ChevronRight class="h-4 w-4" />
          </Button>
        </div>
      {/if}

      <Button
        onclick={() =>
          handleCenterClick(
            detailsStore.currentPoiDetail?.latitude,
            detailsStore.currentPoiDetail?.longitude
          )}
        class="cursor-pointer bg-primary hover:bg-primary/80 rounded-sm {isMultiple
          ? 'w-14'
          : 'w-auto'} sm:w-auto"
      >
        <MapPinned class="h-4 w-4" />
        <span class="{isMultiple ? 'hidden' : 'inline'} sm:inline">
          Mutasd a térképen!
        </span>
      </Button>
    </div>
  </div>

  <!-- Content -->
  <div class="flex-1 overflow-y-auto">
    {#if detailsStore.isLoading}
      <!-- Enhanced Loading State -->
      <div class="flex flex-col items-center justify-center p-8 gap-4">
        <div class="relative animate-spin-slow opacity-60">
          <Hexagon
            class="w-32 h-32 text-black fill-primary"
            strokeWidth={2.5}
          />
          <div class="absolute inset-0 flex items-center justify-center">
            <img src="/assets/ui-icons/beeco-logo.svg" alt="Logo" />
          </div>
        </div>
        <div class="text-center space-y-1">
          <p class="text-sm font-medium">Ökos pontok betöltése...</p>
        </div>
      </div>
    {:else if detailsStore.error}
      <div class="p-4">
        <Card class="border-destructive">
          <CardContent class="p-4">
            <p class="text-destructive text-sm">{detailsStore.error}</p>
            <Button
              variant="outline"
              size="sm"
              class="mt-3"
              onclick={() => detailsStore.refresh()}
            >
              Frissítés
            </Button>
          </CardContent>
        </Card>
      </div>
    {:else if detailsStore.currentPoiDetail}
      {@const poi = detailsStore.currentPoiDetail}
      <div class="space-y-4">
        <!-- Image Carousel -->
        {#if poi.imageUrls && poi.imageUrls.length > 0}
          <div class="w-full">
            <Carousel.Root>
              <Carousel.Content>
                {#each poi.imageUrls as imageUrl}
                  <Carousel.Item>
                    <div class="aspect-video w-full overflow-hidden">
                      <img
                        src={imageUrl}
                        alt={poi.name}
                        class="w-full h-full object-cover"
                      />
                    </div>
                  </Carousel.Item>
                {/each}
              </Carousel.Content>
              {#if poi.imageUrls.length > 1}
                <Carousel.Previous
                  class="absolute left-2 top-1/2 -translate-y-1/2"
                />
                <Carousel.Next
                  class="absolute right-2 top-1/2 -translate-y-1/2"
                />
              {/if}
            </Carousel.Root>
          </div>
        {:else}
          <div
            class="aspect-video w-full bg-muted flex items-center justify-center"
          >
            <span class="text-muted-foreground">Nincs elérhető fénykép</span>
          </div>
        {/if}

        <div class="relative p-4 space-y-4">
          <!-- Header Info -->
          <div>
            <div class="flex items-start justify-between gap-2 mb-2">
              <div class="w-full flex text-xl pb-2 gap-2">
                {#await poiIcon}
                  <Hexagon />
                {:then icon}
                  <img src={icon} alt="icon" />
                {/await}
                <div>
                  <h1 class="text-2xl">
                    {poi.name}
                  </h1>
                  <p class="text-base font-light">{poi.mainService}</p>
                </div>
              </div>
              {#if poi.verified === "VERIFIED"}
                <BadgeCheck
                  size={32}
                  class="absolute right-4 top-4 fill-primary text-foreground"
                />
              {/if}
            </div>
            <p class="text-sm font-sans font-semibold">{poi.bio}</p>

            <!-- Rating -->
            {#if poi.rating && poi.ratingNumber}
              <RatingsPopover />
            {/if}
          </div>

          {#if poi.hasCoupon}
            <div class="flex justify-start">
              <Coupon
                text={"Kedvezmények elérhetők"}
                onclick={handleCouponClick}
              />
            </div>
          {/if}

          {#if poi.address}
            <div class="text-muted-foreground mt-4">
              <div class="flex text-2xl items-center">
                <HungaryPointIcon />
                Hol találod?
              </div>
              <span class="font-sans text-foreground">{poi.address}</span>
              <p hidden={!distance} class="text-base font-sans text-foreground">
                Távolság: {distance}
              </p>
            </div>
          {/if}

          <div class="flex justify-start">
            <Button
              class="w-full max-w-sm cursor-pointer rounded-sm bg-primary/80 hover:bg-primary font-extrabold"
              href={`https://www.google.com/maps/dir/?api=1&destination=${poi.latitude},${poi.longitude}&travelmode=bicycling`}
              target="_blank"
              ><Navigation class="!h-5 !w-5" />Útvonal tervezés</Button
            >
          </div>

          <!-- Description -->
          {#if poi.longDescription || poi.bio}
            <div>
              <div class="w-full gap-1 flex text-xl items-center pb-2">
                <DetailsIcon />
                Ismerd meg
              </div>
              <p
                class="text-sm text-muted-foreground break-words font-sans font-semibold leading-relaxed"
              >
                {poi.longDescription || poi.bio}
              </p>
            </div>
          {/if}

          <!-- Tags -->
          {#if poi.tags && poi.tags.length > 0}
            <div>
              <div class="w-full flex text-xl gap-1 items-center pb-2">
                <LightBulbIcon />Miért ökos?
              </div>
              <div class="flex flex-wrap gap-1 mt-2">
                {#each poi.tags as tag}
                  <Badge
                    variant="outline"
                    class="bg-white rounded-none text-xs font-semibold"
                    >{tag}</Badge
                  >
                {/each}
              </div>
            </div>
          {/if}

          <!-- Contact Information -->
          <div>
            {#if poi.contactPersonPhone || poi.email || poi.website || poi.facebook || poi.instagram || poi.tiktok}
              <div class="w-full flex text-xl gap-1 items-center pb-2">
                <PhoneClick />
                Kövess és keress minket
              </div>
            {/if}
            <div class="space-y-2">
              {#if poi.contactPersonPhone}
                <Button
                  variant="ghost"
                  class="w-full justify-start h-auto hover:bg-primary"
                  onclick={() => handlePhoneClick(poi?.contactPersonPhone)}
                >
                  <Phone class="h-4 w-4 mr-2 fill-primary" />
                  {poi.contactPersonPhone}
                </Button>
              {/if}

              {#if poi.email}
                <Button
                  variant="ghost"
                  class="w-full justify-start h-auto hover:bg-primary"
                  onclick={() => handleEmailClick(poi?.email)}
                >
                  <Mail class="h-4 w-4 mr-2" />
                  {poi.email}
                </Button>
              {/if}

              {#if poi.website}
                <Button
                  variant="ghost"
                  class="w-full justify-start h-auto hover:bg-primary"
                  onclick={() => handleSocialClick(poi?.website)}
                >
                  <Globe class="h-4 w-4 mr-2 fill-primary" />
                  Weboldal
                </Button>
              {/if}

              <!-- Social Media -->
              {#snippet SocialMedia(type: "facebook" | "instagram" | "tiktok")}
                <Button
                  variant="ghost"
                  class="aspect-square p-0 hover:bg-black"
                  onclick={() => handleSocialClick(poi?.[type])}
                >
                  <img src={`/assets/ui-icons/${type}.svg`} alt={type} />
                </Button>
              {/snippet}
              <div class="flex gap-4 mt-2">
                {#if poi.facebook}
                  {@render SocialMedia("facebook")}
                {/if}

                {#if poi.instagram}
                  {@render SocialMedia("instagram")}
                {/if}

                {#if poi.tiktok}
                  {@render SocialMedia("tiktok")}
                {/if}
              </div>
            </div>
          </div>

          <!-- Opening Hours -->
          {#if poi.openingHours}
            <Separator />
            <OpeningHours hoursData={poi.openingHours} />
          {/if}

          <!-- Event Dates -->
          {#if poi.eventStartDate || poi.eventEndDate}
            <Separator />
            <div>
              <h3 class="mb-2">Részletek</h3>
              <div class="space-y-1 text-sm">
                {#if poi.eventStartDate}
                  <div class="flex items-center gap-2">
                    <Calendar class="h-4 w-4" />
                    <span>Kezdés: {formatDate(poi.eventStartDate)}</span>
                  </div>
                {/if}
                {#if poi.eventEndDate}
                  <div class="flex items-center gap-2">
                    <Calendar class="h-4 w-4" />
                    <span>Vége: {formatDate(poi.eventEndDate)}</span>
                  </div>
                {/if}
              </div>
            </div>
          {/if}

          <!-- Coupons -->
          {#if poi.coupons && poi.coupons.length > 0}
            <Separator />
            <div>
              <h3 class="mb-3">Aktuális kínálat</h3>
              <div class="space-y-2">
                {#each poi.coupons as poiCoupon}
                  <Card>
                    <CardContent class="p-3">
                      <div class="flex items-start gap-3">
                        <Gift class="h-5 w-5 text-primary mt-0.5" />
                        <div class="flex-1">
                          <h4 class="text-sm">
                            {poiCoupon.coupon.text}
                          </h4>
                          {#if poiCoupon.coupon.description}
                            <p class="text-xs text-muted-foreground mt-1">
                              {poiCoupon.coupon.description}
                            </p>
                          {/if}
                          <div class="flex items-center gap-2 mt-2">
                            <Badge variant="secondary" class="text-xs">
                              {poiCoupon.userCouponStatus}
                            </Badge>
                            {#if poiCoupon.coupon.validTo}
                              <span class="text-xs text-muted-foreground">
                                Érvényes: {formatDate(poiCoupon.coupon.validTo)}
                              </span>
                            {/if}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                {/each}
              </div>
            </div>
          {/if}

          <!-- Plant Details -->
          {#if poi.plantDetail}
            <Separator />
            <div>
              <h3 class="mb-2">Növény info</h3>
              <div class="flex items-center gap-2 text-sm">
                <Leaf class="h-4 w-4 text-green-500" />
                <span>{poi.plantDetail.species}</span>
              </div>
            </div>
          {/if}

          <!-- Watering Info -->
          {#if poi.lastWatering || poi.wateredByDifferentUsersCount}
            <Separator />
            <div>
              <h3 class="mb-2">Közösségi program</h3>
              <div class="space-y-2 text-sm">
                {#if poi.wateredByDifferentUsersCount}
                  <div class="flex items-center gap-2">
                    <Droplets class="h-4 w-4 text-blue-500" />
                    <span
                      >Megöntözte {poi.wateredByDifferentUsersCount} személy</span
                    >
                  </div>
                {/if}
                {#if poi.lastWatering}
                  <div class="text-xs text-muted-foreground">
                    Utolsó öntözés: {formatDate(poi.lastWatering.createdAt)}
                  </div>
                {/if}
              </div>
            </div>
          {/if}
        </div>
      </div>
    {:else}
      <div class="flex flex-col items-center justify-center p-8 gap-3">
        <Hexagon class="h-12 w-12 text-muted-foreground/30" />
        <span class="text-muted-foreground text-sm"
          >Nincs Ökos pont kiválasztva</span
        >
      </div>
    {/if}
  </div>
</div>
